import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { SignedPdfButton, signedKeys } from "@/components/onboarding/SignedPdf";
import { Plus, FileText, Clock, CheckCircle2, Trash2, Copy, Send, RotateCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import OnboardingWizard from "./OnboardingWizard";

const STATUS_LABEL = {
  draft:  { label: "Draft",             cls: "border-[var(--line)] text-[var(--ink-2)] bg-white", icon: FileText },
  sent:   { label: "Awaiting signature", cls: "border-[#7C3AED] text-[#7C3AED] bg-[#F5F3FF]",     icon: Clock },
  signed: { label: "Signed",             cls: "border-[var(--ok)] text-[var(--ok)] bg-[var(--ok-bg)]", icon: CheckCircle2 },
};

export default function Onboarding() {
  const [onboardings, setOnboardings] = useState([]);
  const [tab, setTab] = useState("drafts"); // drafts | engaged
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null); // onboarding doc being edited
  const nav = useNavigate();

  async function load() {
    const r = await api.get("/onboarding");
    setOnboardings(r.data);
  }
  useEffect(() => { load(); }, []);

  const drafts  = onboardings.filter((o) => o.status !== "signed");
  const engaged = onboardings.filter((o) => o.status === "signed");
  const expiredCount = onboardings.filter((o) => o.expired).length;
  const list = tab === "drafts" ? drafts : engaged;

  async function copySignLink(o) {
    const token = o.token;
    if (!token) {
      toast.error("No signing link yet — click Send first.");
      return;
    }
    const url = `${window.location.origin}/sign/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Signing link copied to clipboard");
    } catch (e) {
      toast.error("Could not copy — select and copy manually: " + url);
    }
  }

  async function voidOne(o) {
    if (!window.confirm(`Void draft for ${o.client_name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/onboarding/${o.id}`);
      toast.success("Draft deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  }

  async function sendOne(o) {
    if (o.status === "sent" && !window.confirm(`Re-send the proposal to ${o.email}? This restarts the 7-day validity window.`)) return;
    try {
      const r = await api.post(`/onboarding/${o.id}/send`);
      const url = r.data?.sign_url || `${window.location.origin}/sign/${r.data.token}`;
      try { await navigator.clipboard.writeText(url); } catch (e) {}
      toast.success(r.data?.resent ? "Proposal re-sent — new 7-day window started." : "Marked as Sent. Signing link copied to your clipboard.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    }
  }

  if (showNew || editing) {
    return (
      <OnboardingWizard
        existing={editing}
        onClose={() => { setShowNew(false); setEditing(null); load(); }}
      />
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-5">
        <div>
          <h1 className="text-2xl font-serif-strong">Onboarding</h1>
          <p className="text-sm text-[var(--ink-2)] mt-1">Partner-initiated engagement — send a Letter of Engagement, client signs online, client record is created automatically.</p>
        </div>
        <button
          data-testid="onboarding-new-btn"
          onClick={() => setShowNew(true)}
          className="btn-primary flex items-center gap-2 text-sm !py-2 !px-3"
        >
          <Plus size={14} strokeWidth={1.5} /> New Onboarding
        </button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-[var(--line)] mb-4">
        <button
          data-testid="tab-drafts"
          onClick={() => setTab("drafts")}
          className={`px-4 py-2 text-sm border-b-2 -mb-px transition ${tab === "drafts" ? "border-[var(--navy)] text-[var(--navy)] font-medium" : "border-transparent text-[var(--ink-2)]"}`}
        >Drafts & Sent ({drafts.length})</button>
        {expiredCount > 0 && (
          <span data-testid="expired-count" className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[var(--err)] text-[var(--err)] bg-[var(--err-bg)]">
            <AlertTriangle size={10} /> {expiredCount} expired
          </span>
        )}
        <button
          data-testid="tab-engaged"
          onClick={() => setTab("engaged")}
          className={`px-4 py-2 text-sm border-b-2 -mb-px transition ${tab === "engaged" ? "border-[var(--navy)] text-[var(--navy)] font-medium" : "border-transparent text-[var(--ink-2)]"}`}
        >Engaged ({engaged.length})</button>
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="tbl">
          <thead>
            <tr>
              <th>Client</th>
              <th>Plan</th>
              <th className="text-right">Monthly Fee</th>
              <th>Status</th>
              <th>Created</th>
              <th>{tab === "engaged" ? "Signed" : "Sent"}</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((o) => {
              const S = STATUS_LABEL[o.status] || STATUS_LABEL.draft;
              return (
                <tr key={o.id} data-testid={`onboarding-row-${o.id}`}>
                  <td>
                    <div className="font-medium">{o.client_name}</div>
                    <div className="text-xs text-[var(--ink-2)]">{o.email}{o.company_name ? ` · ${o.company_name}` : ""}</div>
                  </td>
                  <td>{o.plan?.label || "—"}</td>
                  <td className="text-right font-mono-data">£{Number(o.monthly_fee || 0).toFixed(2)}</td>
                  <td>
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${S.cls}`}>
                      <S.icon size={10} /> {S.label}
                    </span>
                    {o.status === "sent" && (o.required_docs || []).length > 1 && (
                      <span data-testid={`signed-progress-${o.id}`} className="ml-1 text-[10px] font-mono-data text-[var(--ink-2)]">{(o.signed_docs || []).length}/{o.required_docs.length} signed</span>
                    )}
                    {o.status === "signed" && <DDBadge s={o.dd_status} id={o.id} />}
                    {o.expired && (
                      <span data-testid={`expired-${o.id}`} className="ml-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[var(--err)] text-[var(--err)] bg-[var(--err-bg)]" title="Sent more than 7 days ago and not signed">
                        <AlertTriangle size={10} /> Expired
                      </span>
                    )}
                  </td>
                  <td className="text-xs text-[var(--ink-2)]">{(o.created_at || "").slice(0, 10)}</td>
                  <td className="text-xs text-[var(--ink-2)]">{tab === "engaged" ? (o.signed_at || "").slice(0,10) : (o.sent_at || "").slice(0,10)}</td>
                  <td className="text-right">
                    <div className="inline-flex items-center gap-1">
                      {o.status === "draft" && (
                        <>
                          <button
                            data-testid={`send-${o.id}`}
                            onClick={() => sendOne(o)}
                            className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[var(--navy)] text-[var(--navy)] hover:bg-[var(--navy)]/5 transition"
                            title="Mark as sent + copy signing link"
                          ><Send size={9} /> Send</button>
                          <button
                            data-testid={`edit-${o.id}`}
                            onClick={() => setEditing(o)}
                            className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[var(--line)] text-[var(--ink-2)] hover:bg-[var(--bg-2)] transition"
                          >Edit</button>
                          <button
                            data-testid={`delete-${o.id}`}
                            onClick={() => voidOne(o)}
                            className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[var(--err)] text-[var(--err)] hover:bg-[var(--err-bg)] transition"
                          ><Trash2 size={9} /></button>
                        </>
                      )}
                      {o.status === "sent" && (
                        <>
                          <button
                            data-testid={`copy-link-${o.id}`}
                            onClick={() => copySignLink(o)}
                            className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[var(--navy)] text-[var(--navy)] hover:bg-[var(--navy)]/5 transition"
                            title="Copy signing link"
                          ><Copy size={9} /> Copy link</button>
                          <button
                            data-testid={`resend-${o.id}`}
                            onClick={() => sendOne(o)}
                            className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border transition ${o.expired ? "border-[var(--err)] bg-[var(--err)] text-white hover:opacity-90" : "border-[var(--navy)] text-[var(--navy)] hover:bg-[var(--navy)]/5"}`}
                            title="Re-send proposal and restart the 7-day window"
                          ><RotateCw size={9} /> Re-send</button>
                          <button
                            data-testid={`edit-${o.id}`}
                            onClick={() => setEditing(o)}
                            className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[var(--line)] text-[var(--ink-2)] hover:bg-[var(--bg-2)] transition"
                          >Edit</button>
                        </>
                      )}
                      {signedKeys(o).map((k) => <SignedPdfButton key={k} onboardingId={o.id} docKey={k} />)}
                      {o.status === "signed" && o.token && !["active", "pending"].includes(o.dd_status) && (
                        <button
                          data-testid={`copy-dd-link-${o.id}`}
                          onClick={async () => { try { await navigator.clipboard.writeText(`${window.location.origin}/sign/${o.token}`); } catch (e) {} toast.success("Direct Debit link copied"); }}
                          className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[var(--gold)] text-[var(--warn)] hover:bg-[var(--gold-soft)] transition"
                          title="Copy the link the client uses to set up their Direct Debit"
                        ><Copy size={9} /> Copy DD link</button>
                      )}
                      {o.status === "signed" && (
                        <button
                          data-testid={`view-client-${o.id}`}
                          onClick={() => o.client_id && nav("/clients")}
                          className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[var(--ok)] text-[var(--ok)] hover:bg-[var(--ok-bg)] transition"
                        >Open client</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {list.length === 0 && (
              <tr><td colSpan={7} className="text-center py-12 text-[var(--ink-2)]">
                <FileText size={20} strokeWidth={1} className="inline mb-2 opacity-50" /><br />
                {tab === "drafts"
                  ? <>No drafts yet. Click <b>New Onboarding</b> to get started.</>
                  : <>No engaged clients from onboarding yet.</>}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const DD = {
  awaiting: ["Awaiting DD", "border-[var(--gold)] text-[var(--warn)] bg-[var(--gold-soft)]"],
  setup_started: ["Awaiting DD", "border-[var(--gold)] text-[var(--warn)] bg-[var(--gold-soft)]"],
  pending: ["DD pending", "border-[#0369A1] text-[#0369A1] bg-[#F0F9FF]"],
  active: ["DD active", "border-[var(--ok)] text-[var(--ok)] bg-[var(--ok-bg)]"],
  failed: ["DD failed", "border-[var(--err)] text-[var(--err)] bg-[var(--err-bg)]"],
  cancelled: ["DD cancelled", "border-[var(--err)] text-[var(--err)] bg-[var(--err-bg)]"],
};

export function DDBadge({ s, id }) {
  const [label, cls] = DD[s] || DD.awaiting;
  return <span data-testid={`dd-badge-${id}`} className={`ml-1 inline-flex text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${cls}`}>{label}</span>;
}
